import { useMemo } from "react";

function SandboxRight({
 activePreviewTab,
 setActivePreviewTab,
 leagues,
 teams,
 players,
 scheduleGames,
 selectedLeagueForStandings,
 setSelectedLeagueForStandings,
 playerSearch,
 setPlayerSearch,
 selectedLeagueForPlayers,
 setSelectedLeagueForPlayers,
 selectedTeamForPlayers,
 setSelectedTeamForPlayers,
 teamsForSelectedPlayerLeague,
 standingsTeams,
 selectedLeagueForSchedule,
 setSelectedLeagueForSchedule,
 selectedTeamPreviewId,
 setSelectedTeamPreviewId,
 selectedGamePreviewId,
 setSelectedGamePreviewId,
 playerGameStats = [],
}) {
 const getTeamName = (teamId) => {
  return teams.find((item) => String(item.id) === String(teamId))?.name || "";
 };

 const getLeagueName = (leagueId) => {
  return leagues.find((item) => String(item.id) === String(leagueId))?.name || "";
 };

 const formatGameDate = (value) => {
  if (!value) return "—";

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${month}/${day}/${year}`;
 };

 const formatGameTime = (value) => {
  if (!value) return "—";

  const [rawHour, rawMinute] = value.split(":");
  const hourNum = Number(rawHour);

  if (Number.isNaN(hourNum) || rawMinute == null) return value;

  const suffix = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;

  return `${hour12}:${rawMinute} ${suffix}`;
 };

 const filteredPlayers = players.filter((player) => {
  const team = teams.find((item) => item.id === player.teamId);
  if (!team) return false;

  const matchesSearch = player.name
   .toLowerCase()
   .includes(playerSearch.toLowerCase());

  const matchesLeague = selectedLeagueForPlayers
   ? String(team.leagueId) === String(selectedLeagueForPlayers)
   : true;

  const matchesTeam = selectedTeamForPlayers
   ? String(player.teamId) === String(selectedTeamForPlayers)
   : true;

  return matchesSearch && matchesLeague && matchesTeam;
 });

 const selectedTeamPreview =
  teams.find((item) => String(item.id) === String(selectedTeamPreviewId)) || null;
 const selectedTeamPreviewGames = selectedTeamPreview
  ? scheduleGames
   .filter(
    (game) =>
     String(game.homeTeamId) === String(selectedTeamPreview.id) ||
     String(game.awayTeamId) === String(selectedTeamPreview.id)
   )
   .sort((a, b) => new Date(a.date) - new Date(b.date))
  : [];

 const selectedTeamPreviewPlayers = selectedTeamPreview
  ? players.filter(
   (player) => String(player.teamId) === String(selectedTeamPreview.id)
  )
  : [];

 const selectedGamePreview =
  scheduleGames.find((item) => String(item.id) === String(selectedGamePreviewId)) ||
  null;

 const getPlayerStatForGame = (gameId, playerId) => {
  return (
   playerGameStats.find(
    (item) =>
     String(item.gameId) === String(gameId) &&
     String(item.playerId) === String(playerId)
   ) || { pts: 0 }
  );
 };

 const getTeamPlayersForGame = (game, teamId) => {
  return players.filter((player) => String(player.teamId) === String(teamId));
 };

 const getTeamTotalsForGame = (game, teamId) => {
  const teamPlayers = getTeamPlayersForGame(game, teamId);

  return teamPlayers.reduce(
   (acc, player) => {
    const stat = getPlayerStatForGame(game.id, player.id);
    acc.pts += Number(stat.pts) || 0;
    return acc;
   },
   { pts: 0 }
  );
 };

 const computedStandings = useMemo(() => {
  return standingsTeams
   .map((team) => {
    let wins = 0;
    let losses = 0;

    scheduleGames.forEach((game) => {
     const isHome = String(game.homeTeamId) === String(team.id);
     const isAway = String(game.awayTeamId) === String(team.id);

     if (!isHome && !isAway) return;
     if (game.status !== "Completed") return;

     const homeScore = Number(game.homeScore) || 0;
     const awayScore = Number(game.awayScore) || 0;

     if (homeScore === awayScore) return;

     if (isHome) {
      if (homeScore > awayScore) wins += 1;
      if (homeScore < awayScore) losses += 1;
     }

     if (isAway) {
      if (awayScore > homeScore) wins += 1;
      if (awayScore < homeScore) losses += 1;
     }
    });

    return {
     ...team,
     wins,
     losses,
    };
   })
   .sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.losses - b.losses;
   });
 }, [standingsTeams, scheduleGames]);

 const renderPublicGameTable = (game, teamId, label, score) => {
  const teamPlayers = getTeamPlayersForGame(game, teamId);
  const totals = getTeamTotalsForGame(game, teamId);

  return (
   <div className="sandbox-public-game-section">
    <h3 className="sandbox-public-game-team-title">
     {label} ({score})
    </h3>

    <div className="sandbox-table-wrap">
     <table className="sandbox-table">
      <thead>
       <tr>
        <th>Player</th>
        <th>PTS</th>
       </tr>
      </thead>
      <tbody>
       {teamPlayers.length === 0 ? (
        <tr>
         <td colSpan="2">No players yet.</td>
        </tr>
       ) : (
        teamPlayers.map((player) => {
         const stat = getPlayerStatForGame(game.id, player.id);

         return (
          <tr key={player.id}>
           <td>{player.name}</td>
           <td>{stat.pts || 0}</td>
          </tr>
         );
        })
       )}

       {teamPlayers.length > 0 && (
        <tr className="sandbox-admin-total-row">
         <td>Total</td>
         <td>{totals.pts}</td>
        </tr>
       )}
      </tbody>
     </table>
    </div>
   </div>
  );
 };

 return (
  <div className="sandbox-right-page">
   <div className="sandbox-preview-shell">
    <div className="sandbox-preview-nav">
     <button
      type="button"
      className={`sandbox-preview-link ${!selectedTeamPreview && !selectedGamePreview && activePreviewTab === "standings" ? "active" : ""}`}
      onClick={() => {
       setActivePreviewTab("standings");
       setSelectedTeamPreviewId(null);
       setSelectedGamePreviewId(null);
      }}
     >
      Standings
     </button>

     <button
      type="button"
      className={`sandbox-preview-link ${!selectedTeamPreview && !selectedGamePreview && activePreviewTab === "players" ? "active" : ""}`}
      onClick={() => {
       setActivePreviewTab("players");
       setSelectedTeamPreviewId(null);
       setSelectedGamePreviewId(null);
      }}
     >
      Players
     </button>

     <button
      type="button"
      className={`sandbox-preview-link ${!selectedTeamPreview && !selectedGamePreview && activePreviewTab === "schedule" ? "active" : ""}`}
      onClick={() => {
       setActivePreviewTab("schedule");
       setSelectedTeamPreviewId(null);
       setSelectedGamePreviewId(null);
      }}
     >
      Schedule
     </button>
    </div>

    <div className="sandbox-preview-body">
     {selectedGamePreview && (
      <div className="sandbox-preview-page">
       <div className="sandbox-public-game-header">
        <div className="sandbox-public-game-meta">
         <div className="sandbox-public-game-location">
          {selectedGamePreview.location}
         </div>
         <div className="sandbox-public-game-datetime">
          {formatGameDate(selectedGamePreview.date)} •{" "}
          {formatGameTime(selectedGamePreview.time)}
         </div>
        </div>

        <div className="sandbox-public-scoreboard">
         <div className="sandbox-public-score-team">
          {getTeamName(selectedGamePreview.homeTeamId)}
         </div>
         <div className="sandbox-public-score-number">
          {selectedGamePreview.homeScore ?? 0}
         </div>
         <div className="sandbox-public-score-final">FINAL</div>
         <div className="sandbox-public-score-number">
          {selectedGamePreview.awayScore ?? 0}
         </div>
         <div className="sandbox-public-score-team">
          {getTeamName(selectedGamePreview.awayTeamId)}
         </div>
        </div>
       </div>

       {renderPublicGameTable(
        selectedGamePreview,
        selectedGamePreview.homeTeamId,
        `Home Team: ${getTeamName(selectedGamePreview.homeTeamId)}`,
        selectedGamePreview.homeScore ?? 0
       )}

       {renderPublicGameTable(
        selectedGamePreview,
        selectedGamePreview.awayTeamId,
        `Away Team: ${getTeamName(selectedGamePreview.awayTeamId)}`,
        selectedGamePreview.awayScore ?? 0
       )}
      </div>
     )}
     {!selectedGamePreview && selectedTeamPreview && (
      <div className="sandbox-preview-page sandbox-team-detail-page">
       <h1 className="sandbox-team-detail-title">{selectedTeamPreview.name}</h1>

       <div className="sandbox-team-detail-section">
        <h3 className="sandbox-team-detail-section-title">SCHEDULE</h3>

        <div className="sandbox-table-wrap">
         <table className="sandbox-table">
          <thead>
           <tr>
            <th>Date</th>
            <th>Matchup</th>
            <th>Results</th>
           </tr>
          </thead>
          <tbody>
           {selectedTeamPreviewGames.length === 0 ? (
            <tr>
             <td colSpan="3">No games yet.</td>
            </tr>
           ) : (
            selectedTeamPreviewGames.map((game) => (
             <tr key={game.id}>
              <td>{formatGameDate(game.date)}</td>
              <td>
               <button
                type="button"
                className="sandbox-team-link-btn"
                onClick={() => setSelectedGamePreviewId(game.id)}
               >
                {getTeamName(game.homeTeamId)} vs{" "}
                {getTeamName(game.awayTeamId)}
               </button>
              </td>
              <td>{game.results}</td>
             </tr>
            ))
           )}
          </tbody>
         </table>
        </div>
       </div>

       <div className="sandbox-team-detail-section">
        <h3 className="sandbox-team-detail-section-title">ROSTER</h3>

        <div className="sandbox-table-wrap">
         <table className="sandbox-table">
          <thead>
           <tr>
            <th>Name</th>
           </tr>
          </thead>
          <tbody>
           {selectedTeamPreviewPlayers.length === 0 ? (
            <tr>
             <td>No players yet.</td>
            </tr>
           ) : (
            selectedTeamPreviewPlayers.map((player) => (
             <tr key={player.id}>
              <td>{player.name}</td>
             </tr>
            ))
           )}
          </tbody>
         </table>
        </div>
       </div>
      </div>
     )}
     {!selectedGamePreview && !selectedTeamPreview && activePreviewTab === "standings" && (
      <div className="sandbox-preview-page">
       <div className="sandbox-standings-toolbar">
        <select
         className="sandbox-standings-select"
         value={selectedLeagueForStandings}
         onChange={(e) => setSelectedLeagueForStandings(e.target.value)}
        >
         <option value="all">All Leagues</option>
         {leagues.map((league) => (
          <option key={league.id} value={league.id}>
           {league.name}
          </option>
         ))}
        </select>
       </div>

       {computedStandings.length === 0 ? (
        <div className="sandbox-empty-state">No teams yet.</div>
       ) : (
        <div className="sandbox-table-wrap">
         <table className="sandbox-table">
          <thead>
           <tr>
            <th>Position</th>
            <th>Team</th>
            {selectedLeagueForStandings === "all" && <th>League</th>}
            <th>Wins</th>
            <th>Losses</th>
           </tr>
          </thead>
          <tbody>
           {computedStandings.map((team, index) => (
            <tr key={team.id}>
             <td>{index + 1}</td>
             <td>
              <button
               type="button"
               className="sandbox-team-link-btn"
               onClick={() => {
                setActivePreviewTab("players");
                setSelectedTeamPreviewId(String(team.id));
                setSelectedGamePreviewId(null);
               }}
              >
               {team.name}
              </button>
             </td>
             {selectedLeagueForStandings === "all" && (
              <td>{getLeagueName(team.leagueId)}</td>
             )}
             <td>{team.wins}</td>
             <td>{team.losses}</td>
            </tr>
           ))}
          </tbody>
         </table>
        </div>
       )}
      </div>
     )}

     {!selectedGamePreview && !selectedTeamPreview && activePreviewTab === "players" && (
      <div className="sandbox-preview-page">
       <div className="sandbox-player-filters">
        <input
         className="sandbox-player-search"
         type="text"
         placeholder="Search player name..."
         value={playerSearch}
         onChange={(e) => setPlayerSearch(e.target.value)}
        />

        <div className="sandbox-player-filter-row">
         <select
          className="sandbox-player-filter-select"
          value={selectedLeagueForPlayers}
          onChange={(e) => {
           setSelectedLeagueForPlayers(e.target.value);
           setSelectedTeamForPlayers("");
           setSelectedTeamPreviewId(null);
           setSelectedGamePreviewId(null);
          }}
         >
          <option value=""> ALL LEAGUES </option>
          {leagues.map((league) => (
           <option key={league.id} value={league.id}>
            {league.name}
           </option>
          ))}
         </select>

         <select
          className="sandbox-player-filter-select"
          value={selectedTeamForPlayers}
          onChange={(e) => {
           setSelectedTeamForPlayers(e.target.value);
           setSelectedTeamPreviewId(null);
           setSelectedGamePreviewId(null);
          }}
         >
          <option value=""> ALL TEAMS </option>
          {teamsForSelectedPlayerLeague.map((team) => (
           <option key={team.id} value={team.id}>
            {team.name}
           </option>
          ))}
         </select>
        </div>
       </div>

       {filteredPlayers.length === 0 ? (
        <div className="sandbox-empty-state">No players found.</div>
       ) : (
        <div className="sandbox-player-card-grid">
         {filteredPlayers.map((player) => (
          <div key={player.id} className="sandbox-player-card">
           <div className="sandbox-player-card-image" />
           <div className="sandbox-player-card-name">{player.name}</div>
          </div>
         ))}
        </div>
       )}
      </div>
     )}

     {!selectedGamePreview && !selectedTeamPreview && activePreviewTab === "schedule" && (
      <div className="sandbox-preview-page">
       <div className="sandbox-standings-toolbar">
        <select
         className="sandbox-standings-select"
         value={selectedLeagueForSchedule}
         onChange={(e) => setSelectedLeagueForSchedule(e.target.value)}
        >
         <option value="all">All Leagues</option>
         {leagues.map((league) => (
          <option key={league.id} value={league.id}>
           {league.name}
          </option>
         ))}
        </select>
       </div>

       {scheduleGames.length === 0 ? (
        <div className="sandbox-empty-state">No games in this league yet.</div>
       ) : (
        <div className="sandbox-table-wrap">
         <table className="sandbox-table">
          <thead>
           <tr>
            <th>Date</th>
            {selectedLeagueForSchedule === "all" && <th>League</th>}
            <th>Matchup</th>
            <th>Location</th>
            <th>Time</th>
            <th>Status</th>
            <th>Results</th>
           </tr>
          </thead>
          <tbody>
           {scheduleGames.map((game) => (
            <tr key={game.id}>
             <td>{formatGameDate(game.date)}</td>
             {selectedLeagueForSchedule === "all" && (
              <td>{getLeagueName(game.leagueId)}</td>
             )}
             <td>
              <button
               type="button"
               className="sandbox-team-link-btn"
               onClick={() => setSelectedGamePreviewId(game.id)}
              >
               {getTeamName(game.homeTeamId)} vs{" "}
               {getTeamName(game.awayTeamId)}
              </button>
             </td>
             <td>{game.location}</td>
             <td>{formatGameTime(game.time)}</td>
             <td>{game.status}</td>
             <td>{game.results}</td>
            </tr>
           ))}
          </tbody>
         </table>
        </div>
       )}
      </div>
     )}
    </div>
   </div>
  </div>
 );
}

export default SandboxRight;